<template>
  <div
    class="absolute left-0 top-0 z-10 flex h-full min-h-12 w-full items-center justify-center rounded-lg bg-yapgray-300/80 dark:bg-yapdarkgray-700/80"
    v-if="state.schemaLoading"
  >
    <YapLoader :size="YapLoaderSizes.Medium" />
  </div>

  <!-- TODO: disabled until we decide to release international payments feature -->
  <YapInfo
    v-if="false /*state.isIncorePayment && !getCurrentCashAccount?.internationalPaymentsAvailable*/"
    :message="t('webapp.payments.international_payment.not_available.txt')"
    :style="InfoStyle.Warning"
  />
  <YapInfo
    v-if="
      (state.isIncorePayment || (state.isSicPayment && !state.isY2YPayment)) &&
      state.hasFeatureInternationalPaymentSEPAOut === false &&
      state.hasFeatureInternationalPaymentSWIFTOut === false
    "
    :title="t('webapp.payments.not_eligible.upgrade.title')"
    :message="t('webapp.payments.not_eligible.upgrade.txt')"
    :style="InfoStyle.Warning"
  />

  <YapForm
    v-if="!!state.computedSchema && !state.requiredDataLoading"
    :schema="state.computedSchema"
    :method="state.formMethod"
    :uri="state.formUri"
    :data="prepareFormData()"
    :submit-disabled="!state.canContinue"
    custom-error-handling
    @submitted="(resp: any) => paymentFormSubmitted(resp)"
    @submission-error="(errors: any) => (state.submissionError = errors)"
    @errors="(errors: any) => (state.submissionError = errors)"
  >
    <template #form>
      <div class="grid grid-cols-1 gap-4 p-2">
        <div v-if="!state.isInternalPayment">
          <h3 class="text-lg dark:text-primary-dark-100">{{ t('app.payments.beneficiaryCardTitle') }}</h3>
          <div class="divide-y rounded-lg border dark:divide-yapdarkgray-500 dark:border-yapdarkgray-500">
            <YapInput
              :label="t('app.payments.beneficiaryNameLabel')"
              name="name"
              :schema="state.computedSchema.properties.beneficiary.properties.name"
              v-model="state.paymentForm.beneficiary.name"
              :floating="true"
              :grouped="true"
              :readonly="false"
              class="rounded-t-lg border-b-0"
              required
            />
            <YapMaskedInput
              :label="t('app.payments.ibanNumberLabel')"
              name="iban"
              :schema="state.computedSchema.properties.beneficiary.properties.iban"
              v-model="state.paymentForm.beneficiary.iban"
              :floating="true"
              :grouped="true"
              :readonly="!state.canEditIban"
              :mask-options="IbanMaskOptions"
              :required="state.formMethod === Method.post"
              :errors="state.validationErrors"
              unmasked-model
            />
            <YapMaskedInput
              :label="t('app.payments.bic.swift')"
              name="swiftBIC"
              :schema="{ type: 'string', name: 'swiftBIC' }"
              v-model="state.paymentForm.beneficiary.swiftBIC"
              :floating="true"
              :grouped="true"
              :readonly="!state.canEditIban"
              class="rounded-b-lg"
              :mask-options="SwiftCodeMaskOptions"
              :required="state.isSwiftPayment"
              :errors="state.validationErrors"
              unmasked-model
            />
          </div>
        </div>
        <div v-if="state.paymentForm.beneficiary.address && state.displayAddressFields && !state.isInternalPayment">
          <h3 class="text-lg dark:text-primary-dark-100">{{ t('app.payments.beneficiaryAddressCardTitle') }}</h3>
          <YapAddressInputV2
            :schema="state.computedSchema.properties.beneficiary.properties.address"
            v-model="state.paymentForm.beneficiary.address"
            :floating="true"
            :grouped="true"
          />
        </div>
        <div>
          <h3 class="text-lg dark:text-primary-dark-100">{{ t('app.payments.amountCardTitle') }}</h3>
          <div class="divide-y rounded-lg border dark:divide-yapdarkgray-500 dark:border-yapdarkgray-500">
            <div class="relative flex divide-x overflow-visible dark:divide-yapdarkgray-500">
              <div class="w-1/2">
                <CashAccountSelect
                  v-if="selectedCashAccountUid"
                  :title="t('dict.label.from')"
                  :simple-select="false"
                  :grouped="true"
                  :floating="true"
                  v-model="selectedCashAccountUid"
                  :readonly="false"
                  :international-only="state.isIncorePayment"
                  class="rounded-tl-lg"
                />
              </div>
              <div v-if="state.isInternalPayment" class="w-1/2">
                <CashAccountSelect
                  :title="t('dict.label.to')"
                  :simple-select="false"
                  :stop-prefill="true"
                  :grouped="true"
                  :floating="true"
                  v-model="beneficiaryCashAccountUid"
                  :readonly="false"
                  :international-only="state.isIncorePayment"
                  class="rounded-tr-lg"
                />
              </div>
              <div v-if="!state.isInternalPayment" class="flex flex-1 divide-x dark:divide-yapdarkgray-500">
                <YapInput
                  :label="t('dict.amount')"
                  name="amount"
                  :schema="state.computedSchema.properties.amount"
                  v-model="state.paymentForm.amount"
                  :floating="true"
                  :grouped="true"
                  :readonly="false"
                  class="grow"
                  required
                />
                <YapCurrencyPicker
                  v-model="state.paymentForm.currency"
                  :disabled="!state.canEditCurrency"
                  :grouped="true"
                  class="rounded-tr-lg"
                />
              </div>
            </div>
            <div class="flex divide-x dark:divide-yapdarkgray-500">
              <div v-if="state.isInternalPayment" class="flex divide-x dark:divide-yapdarkgray-500">
                <YapInput
                  :label="t('dict.amount')"
                  name="amount"
                  :schema="state.computedSchema.properties.amount"
                  v-model="state.paymentForm.amount"
                  :floating="true"
                  :grouped="true"
                  :readonly="false"
                  class="flex-1 rounded-bl-lg"
                  required
                />
                <div
                  v-if="!state.isIncorePayment"
                  class="flex items-center rounded-tr-lg bg-yapgray-100 pl-1 pr-4 text-yapgray-600 dark:bg-yapdarkgray-900 dark:text-yapgray-200"
                >
                  {{ state.paymentForm.currency }}
                </div>
                <YapCurrencyPicker v-else v-model="state.paymentForm.currency" :grouped="true" class="rounded-tr-lg" />
              </div>
              <YapDatePicker
                name="paymentTime"
                :label="t('app.payments.paymentDate')"
                v-model="state.paymentForm.paymentTime"
                :schema="{
                  ...state.computedSchema.properties.paymentTime,
                  ...{ minimum: today }
                }"
                :clearable="false"
                :min-date="today"
                :floating="true"
                :grouped="true"
                :readonly="!state.canEditPaymentOrder"
                :is-ebill="paymentOrder?.paymentOrderData.isEBillOrder"
                :should-apply-expected-execution-date="state.beneficiaryIsYapster ? false : true"
                :restrict-banking-dates="!state.beneficiaryIsYapster"
                :weekend-allowed="state.beneficiaryIsYapster ? true : undefined"
                :display-format="(d) => paymentDateDisplayFormat(d, state.beneficiaryIsYapster)"
                :class="{
                  'flex-1 rounded-bl-lg': !state.isInternalPayment,
                  'flex-1': state.isInternalPayment
                }"
              />
              <div class="flex h-14 flex-1 items-center justify-center px-12" v-if="state.loadingStandingApprovals">
                <YapLoader :size="YapLoaderSizes.Small" />
              </div>
              <YapModal v-else-if="state.biller">
                <template #trigger="{ openModal }">
                  <StandingApprovalInputTrigger
                    :has-standing-approvals="!!state.biller?.standingApprovals?.length"
                    :open-modal="openModal"
                    :readonly="!state.canEditPaymentOrder"
                    class="flex-1 rounded-br-lg"
                  />
                </template>
                <template #title>{{ t('app.payments.standingApproval.title') }}</template>
                <template #default="{ closeModal }">
                  <StandingApproval
                    :biller="state.biller"
                    :related-biller-uid="state.relatedBillerUid"
                    @saved="standingApprovalUpdated(closeModal)"
                  />
                </template>
              </YapModal>
              <YapInput
                v-else
                name="recurrence"
                :label="t('app.payments.standing_order.txt')"
                v-model="state.paymentForm.recurrence"
                :schema="state.computedSchema.properties.recurrence"
                :floating="true"
                :grouped="true"
                class="flex-1 rounded-br-lg"
              />
            </div>
            <YapInfo
              :message="t('app.payments.standing_order_selection.txt')"
              class="mt-2"
              v-if="state.paymentForm.recurrence !== 'single'"
            />
            <YapIncoreQuote
              v-if="getCurrentCashAccount?.internationalPaymentsAvailable"
              :amount="state.paymentForm.amount"
              :currency="state.paymentForm.currency"
              :target-iban="state.paymentForm.beneficiary.iban"
              :source-iban="state.sourceIban"
              data-testid="incore-quote"
              @incore-quote-loaded="state.incoreQuote = $event"
            />
          </div>
        </div>
        <div>
          <h3 class="text-lg dark:text-primary-dark-100">{{ t('app.payments.detailsCardTitle') }}</h3>
          <div class="divide-y rounded-lg border dark:divide-yapdarkgray-500 dark:border-yapdarkgray-500">
            <YapInput
              :label="t('app.payments.referenceNumberLabel')"
              name="reference"
              :schema="state.computedSchema.properties.structuredReference"
              :floating="true"
              :grouped="true"
              v-model="state.paymentForm.structuredReference"
              :readonly="false"
              class="rounded-t-lg"
            />
            <YapInput
              :label="t('app.payments.messageToBeneficiary')"
              name="paymentReason"
              :schema="state.computedSchema.properties.paymentReason"
              :floating="true"
              :grouped="true"
              v-model="state.paymentForm.paymentReason"
              :readonly="false"
            />

            <YapCategoryPicker
              :current-category="state.paymentForm.category"
              v-model="state.paymentForm.category"
              :grouped="true"
              :fallback-placements="['top-start']"
              class="w-full rounded-b-lg bg-white dark:bg-yapdarkgray-800"
            />
          </div>
        </div>

        <div class="">
          <h3 class="text-lg dark:text-primary-dark-100">
            {{ t('app.payments.internal_note_title_v2') }}
          </h3>
          <div class="mb-2 h-fit">
            <YapMentionable
              :keys="['@', '#']"
              :mentionable-items="transactionsStore.tagsProfile"
              :is-content-editable="true"
              :allowing-new-items="true"
              :offset="6"
              :filtering-disabled="false"
              :insert-space="false"
              :limit="5"
              :caret-height="0"
            >
              <YapInput
                :label="t('app.payments.personalNoteLabel')"
                name="userNote"
                :schema="state.computedSchema.properties.noteText"
                :floating="true"
                :grouped="false"
                v-model="state.paymentForm.noteText"
                :readonly="false"
              />
            </YapMentionable>
          </div>

          <YapFileResourceUploader
            v-if="state.paymentForm.attachedFileUids"
            :dropzone-text="t('webapp.partnership.orderCard.bulk.upload')"
            :instructions-text="t('app.payments.internal_note.receipt.max_file_size') + ' - ' + '(pdf, png, jpg, jpeg)'"
            :file-resources="state.fileResources"
            :max-files="5"
            @uploaded="handleFileResourceUploaded"
            @deleted="handleFileResourceDeleted"
            @renamed="
              (item) => {
                if (state.fileResources) {
                  const index = state.fileResources.findIndex((f) => f.resourceUid === item.resourceUid);
                  if (index !== undefined && index >= 0) {
                    state.fileResources[index].displayName = item.newName;
                  }
                }
              }
            "
          />
        </div>

        <EbillInvoiceDetail :invoice="state.invoice" v-if="state.invoice" />
      </div>
    </template>
    <template #actions="{ submit, submitDisabled, submitting }">
      <div class="mt-10 flex w-full gap-4">
        <YapButton
          v-if="!!!paymentOrder"
          :button-size="ButtonSize.Medium"
          :outlined="true"
          @click="handleCancelPayment"
        >
          {{ t('dict.label.cancel') }}
        </YapButton>
        <YapModal @modal-closed="handleModalClosed">
          <template #trigger="{ openModal }">
            <YapButton
              v-if="state.canDeletePaymentOrder"
              :button-size="ButtonSize.Medium"
              :outlined="true"
              @click="openModal"
            >
              {{ t('dict.label.delete') }}
            </YapButton>
          </template>
          <template #title>{{ t('app.payments.delete_order.txt') }}</template>
          <template #default>
            <div class="flex flex-col items-center justify-center">
              <YapIcon name="close-circle-outline" class="h-20 w-20 text-yapred-500" />
              <div>{{ t('app.payments.delete_confirmation_dialog.txt') }}</div>
            </div>
          </template>
          <template #footer="{ closeModal }">
            <YapButton :button-size="ButtonSize.Medium" :outlined="true" @click="closeModal">
              {{ t('dict.label.close') }}
            </YapButton>
            <YapButton
              :button-size="ButtonSize.Medium"
              :button-style="ButtonStyle.Danger"
              @click="handleDelete(closeModal)"
            >
              {{ t('dict.label.delete') }}
            </YapButton>
          </template>
        </YapModal>
        <YapModal :wide="true" @modal-closed="handleModalClosed">
          <template #trigger="{ openModal }">
            <YapButton
              :loading="submitting"
              :disabled="submitDisabled || !state.canSubmitPayment"
              :button-size="ButtonSize.Medium"
              @click="openModal"
            >
              {{ !!paymentOrder ? t('dict.label.save') : t('dict.label.continue') }}
            </YapButton>
          </template>
          <template #title>{{ t('webapp.payments.payment_confirmation.title') }}</template>
          <template #default>
            <PaymentConfirmation
              :payment-form="state.paymentForm"
              :display-address-fields="state.displayAddressFields"
              :debit-cash-account-uid="selectedCashAccountUid"
              :file-attachments="state.fileResources"
              :incore-quote="state.incoreQuote"
              :errors="state.submissionError"
              v-if="!state.paymentSubmitted && !!selectedCashAccountUid"
            />
            <PaymentCaptured
              v-else
              :payment-form="state.paymentForm"
              :approval-required="state.beneficiaryApprovalRequired"
              :beneficiary-uid-to-be-approved="state.beneficiaryUidToBeApproved"
            />
          </template>
          <template #footer="{ closeModal }">
            <YapButton :button-size="ButtonSize.Medium" :outlined="true" @click="handleCloseModal(closeModal)">
              {{ t('dict.label.close') }}
            </YapButton>
            <YapButton
              v-if="!state.paymentSubmitted"
              :loading="submitting"
              :disabled="submitDisabled"
              :button-size="ButtonSize.Medium"
              :button-style="ButtonStyle.Info"
              @click="submit"
            >
              {{ t('app.payments.confirmPayment') }}
            </YapButton>
          </template>
        </YapModal>
      </div>
    </template>
  </YapForm>
</template>

<script setup lang="ts">
import { merge, cloneDeep, defaults, pick, keys } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { isValidIBAN } from 'ibantools';
import { computed, onMounted, reactive, watch, ref, Ref } from 'vue';

import apiGw from '@/services/apiGw';
import router from '@/router/router';
import { useI18n } from '@/plugins/i18n';
import useForm from '@/composables/useForm';
import { InfoStyle } from '@/types/yapInfo';
import { RouteNames } from '@/router/routes';
import { isEnvDevOrBelow } from '@/utils/env';
import YapInfo from '@/components/YapInfo.vue';
import YapModal from '@/components/YapModal.vue';
import { useRefdataStore } from '@/store/refdataStore';
import { Method, Partial, Schema } from '@/types/general';
import { createResourceUri, getUidsFromUri } from '@/utils/links';
import YapForm from '@/components/form/YapForm.vue';
import { useSchemasStore } from '@/store/schemaStore';
import YapInput from '@/components/form/YapInput.vue';
import { isYapealIban } from '@/utils/formats';
import YapMentionable from '@/components/YapMentionable.vue';
import { factoredYapAmount, isValidUUID, removeEmptyStringFromObject } from '@/utils/general';
import YapDatePicker from '@/components/form/YapDatePicker.vue';
import { useCashAccountsStore } from '@/store/cashAccountsStore';
import { useTransactionsStore } from '@/store/transactionsStore';
import YapMaskedInput from '@/components/form/YapMaskedInput.vue';
import YapCategoryPicker from '@/components/YapCategoryPicker.vue';
import YapAddressInputV2 from '@/components/form/YapAddressInputV2.vue';
import YapIncoreQuote from '@/components/payments/YapIncoreQuote.vue';
import { IncoreQuote } from '@/components/payments/types/incoreQuote';
import { Beneficiary, PaymentType } from '@/components/payments/types/beneficiary';
import { IbanMaskOptions, SwiftCodeMaskOptions } from '@/components/form/types/maskedInput';
import { PaymentFormData } from '@/components/payments/types/payment';
import YapLoader, { YapLoaderSizes } from '@/components/YapLoader.vue';
import PaymentCaptured from '@/components/payments/PaymentCaptured.vue';
import CashAccountSelect from '@/components/payments/CashAccountSelect.vue';
import YapCurrencyPicker from '@/components/payments/YapCurrencyPicker.vue';
import { paymentDateDisplayFormat } from '@/composables/usePaymentDateFormat';
import PaymentConfirmation from '@/components/payments/PaymentConfirmation.vue';
import YapButton, { ButtonSize, ButtonStyle } from '@/components/YapButton.vue';

import { useUserStore } from '@/store/userStore';
import { usePartnershipsStore } from '@/store/partnershipsStore';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import YapIcon from '@/components/YapIcon.vue';
import { Biller } from '@/components/ebill/types/biller';
import { Invoice } from '@/components/payments/types/invoice';
import StandingApproval from '@/components/ebill/StandingApproval.vue';
import EbillInvoiceDetail from '@/components/ebill/EbillInvoiceDetail.vue';
import { PaymentOrderStatus } from '@/components/transactions/types/paymentOrder';
import StandingApprovalInputTrigger from '@/components/ebill/StandingApprovalInputTrigger.vue';
import { NaturalPersonURI } from '@/types/naturalPerson';

import { FileResource } from '@/types/fileResource';
import YapFileResourceUploader from '@/components/fileResource/YapFileResourceUploader.vue';
import { PartnershipURI } from '@/types/partnership';
import usePaymentForm from '@/composables/usePaymentForm';
import usePaymentEntitlement from '@/composables/usePaymentEntitlement';
import usePendingPaymentOrder from '@/composables/usePendingPaymentOrder';
import { PendingPaymentOrderInfo } from '@/components/payments/types/payment';
import { YapInputValidationError } from '@/components/form/types/yapInputValidationError';
import { useSubscriptionsStore } from '@/store/subscriptionsStore';
import { PlanFeatures } from '@/components/billing/types/subscription';

const emits = defineEmits(['payment-submitted', 'payment-updated', 'payment-deleted', 'submitted-and-modal-closed']);

const props = defineProps<{
  paymentOrder?: PendingPaymentOrderInfo;
  paymentOrderPrefill?: Partial<PendingPaymentOrderInfo>;
  beneficiaryIban?: string;
  beneficiaryUid?: string;
  beneficiaryName?: string;
  attachedFileResourceUid?: string;
}>();

const { t } = useI18n();
const userStore = useUserStore();
const schemaStore = useSchemasStore();
const partnershipsStore = usePartnershipsStore();
const cashAccountsStore = useCashAccountsStore();
const transactionsStore = useTransactionsStore();
const contextSwitchStore = useContextSwitchStore();
const subscriptionsStore = useSubscriptionsStore();
const { isIncorePayment, isY2YPayment, isSicPayment, isSwiftPayment } = usePaymentForm();
const { canEditPendingPaymentOrder, canDeletePendingPaymentOrder, deleteAction } = usePendingPaymentOrder();

const { getCurrentCashAccount, selectedCashAccountUid } = storeToRefs(cashAccountsStore);
const { mandatoryFieldValidator, validateSchemaPatterns } = useForm();

const today = new Date().toISOString();

const beneficiaryCashAccountUid: Ref<string | undefined> = ref();

interface State {
  beneficiary?: Beneficiary;
  schema?: Schema;
  computedSchema?: Schema; // Use of computed schema because we need to stir in the address fields if necessary.
  schemaLoading: boolean;
  requiredDataLoading: boolean;
  attachedFilesUploading: boolean;
  formMethod: Method;
  formUri: string;
  paymentSubmitted: boolean;
  beneficiaryApprovalRequired: boolean;
  beneficiaryUidToBeApproved?: string;
  beneficiaryCashAccountUid?: string;
  paymentForm: PaymentFormData;
  beneficiaryIsYapster: boolean;
  submissionError?: { code: string };
  validationErrors: YapInputValidationError[];
  canEditPaymentOrder?: boolean;
  canDeletePaymentOrder?: boolean;
  canSubmitPayment?: boolean;
  invoice?: Invoice;
  loadingStandingApprovals: boolean;
  biller?: Biller;
  relatedBillerUid?: string;
  canContinue: boolean;
  displayAddressFields: boolean;
  canEditIban: boolean;
  canEditCurrency: boolean;
  canCreateDraftPaymentOrder: boolean;
  paymentOrderUid?: string;
  paymentOrder?: PendingPaymentOrderInfo;
  fileResources?: FileResource[];
  isY2YPayment: boolean;
  isSicPayment: boolean;
  isIncorePayment: boolean;
  isSwiftPayment: boolean;
  isInternalPayment: boolean;
  sourceIban?: string;
  incoreQuote?: IncoreQuote;
  hasFeatureDomesticPayment?: boolean;
  hasFeatureInternationalPaymentSEPAOut?: boolean;
  hasFeatureInternationalPaymentSWIFTOut?: boolean;
}

const state: State = reactive({
  beneficiary: undefined,
  schema: undefined,
  computedSchema: computed(() => {
    if (!state.schema) {
      return undefined;
    }
    if (!state.displayAddressFields || state.beneficiaryIsYapster) {
      return state.schema;
    } else {
      if (state.isIncorePayment) {
        return merge(cloneDeep(state.schema), {
          properties: {
            beneficiary: {
              required: [...state.schema.properties.beneficiary.required, 'address'],
              properties: {
                address: {
                  required: [...state.schema.properties.beneficiary.properties.address.required, 'street']
                }
              }
            }
          }
        });
      } else {
        return merge(cloneDeep(state.schema), {
          properties: {
            beneficiary: {
              required: [...state.schema.properties.beneficiary.required, 'address']
            }
          }
        });
      }
    }
  }),
  schemaLoading: true,
  requiredDataLoading: true,
  attachedFilesUploading: false,
  formMethod: computed(() => (!!state.paymentOrderUid ? Method.patch : Method.post)),
  formUri: computed(() =>
    !!state.paymentOrderUid
      ? `/v2/iban-payment-orders/${state.paymentOrderUid}`
      : `/v2/cash-accounts/${selectedCashAccountUid.value}/iban-payment-orders`
  ),
  paymentSubmitted: false,
  beneficiaryApprovalRequired: false,
  beneficiaryUidToBeApproved: undefined,
  beneficiaryCashAccountUid: undefined,
  paymentForm: {
    beneficiary: {
      uid: undefined,
      name: '',
      iban: '',
      swiftBIC: '',
      address: undefined,
      unstructuredAddress: undefined
    },
    amount: '',
    currency: '',
    structuredReference: undefined,
    paymentReason: undefined,
    paymentTime: props.paymentOrder ? '' : today,
    recurrence: 'single',
    category: 'general',
    accountUid: undefined,
    paymentType: undefined,
    e2eReference: undefined,
    referenceType: undefined,
    customerTransactionId: undefined,
    noteText: undefined,
    attachedFileUids: []
  },
  submissionError: undefined,
  validationErrors: [],
  canEditPaymentOrder: computed(() => {
    if (props.paymentOrder && state.paymentOrder) {
      return canEditPendingPaymentOrder(state.paymentOrder);
    } else {
      return true;
    }
  }),
  canDeletePaymentOrder: computed(() => {
    return state.paymentOrder && canDeletePendingPaymentOrder(state.paymentOrder);
  }),
  canSubmitPayment: computed(() => {
    return (
      isY2YPayment(state.paymentForm) ||
      (isSicPayment(state.paymentForm) && state.hasFeatureDomesticPayment) ||
      (isIncorePayment(state.paymentForm) &&
        getCurrentCashAccount?.value?.internationalPaymentsAvailable &&
        (state.hasFeatureInternationalPaymentSEPAOut || state.hasFeatureInternationalPaymentSWIFTOut))
    );
  }),
  invoice: undefined,
  loadingStandingApprovals: false,
  biller: undefined,
  relatedBillerUid: undefined,
  beneficiaryIsYapster: computed(
    () =>
      (!!state.paymentForm.beneficiary && isYapealIban(state.paymentForm.beneficiary.iban)) || state.isInternalPayment
  ),
  canContinue: computed(
    () =>
      !!state.computedSchema &&
      mandatoryFieldValidator(state.paymentForm, state.computedSchema, false) &&
      validateSchemaPatterns(state.paymentForm, state.computedSchema, false) &&
      (!state.isSwiftPayment || state.paymentForm.beneficiary.swiftBIC !== '')
  ),
  displayAddressFields: computed(() => {
    if (state.beneficiaryIsYapster) {
      return false;
    }
    const addressRequired = !state.beneficiaryIsYapster;
    const hasUnstructuredAddress = !!state.paymentForm.beneficiary.unstructuredAddress?.country;
    return (
      !hasUnstructuredAddress &&
      (addressRequired ||
        state.beneficiary?.address !== undefined ||
        state.paymentForm.beneficiary.address !== undefined)
    );
  }),
  canEditIban: computed(() => !isValidUUID(router.currentRoute.value.params.id?.toString())),
  canEditCurrency: computed(() => {
    return state.paymentOrder === undefined && !!getCurrentCashAccount?.value?.internationalPaymentsAvailable;
  }),
  canCreateDraftPaymentOrder: computed(() => {
    if (!!props.paymentOrder) {
      return false;
    }
    if (
      !!state.paymentForm.amount &&
      !!state.paymentForm.currency &&
      !!state.paymentForm.beneficiary.iban &&
      !!selectedCashAccountUid.value
    ) {
      return true;
    }
    return false;
  }),
  paymentOrder: props.paymentOrder,
  paymentOrderUid: !!props.paymentOrder ? props.paymentOrder.uid : undefined,
  fileResources: undefined,
  isY2YPayment: computed(() => isY2YPayment(state.paymentForm)),
  isSicPayment: computed(() => isSicPayment(state.paymentForm)),
  isIncorePayment: computed(() => isIncorePayment(state.paymentForm)),
  isSwiftPayment: computed(() => isSwiftPayment(state.paymentForm)),
  isInternalPayment: computed(() => {
    return router.currentRoute.value.query.paymentType?.toString() === 'a2a';
  }),
  sourceIban: computed(() =>
    selectedCashAccountUid.value ? cashAccountsStore.getIbanForCashAccountUid(selectedCashAccountUid.value) : undefined
  ),
  incoreQuote: undefined,
  hasFeatureDomesticPayment: undefined,
  hasFeatureInternationalPaymentSEPAOut: undefined,
  hasFeatureInternationalPaymentSWIFTOut: undefined
});

onMounted(async () => {
  loadData();
  transactionsStore.getTagsProfile();

  state.hasFeatureDomesticPayment = await subscriptionsStore.currentContextHasFeature(
    PlanFeatures.FeatureDomesticPayment
  );
  state.hasFeatureInternationalPaymentSEPAOut = await subscriptionsStore.currentContextHasFeature(
    PlanFeatures.FeatureInternationalPaymentSEPAOut
  );
  state.hasFeatureInternationalPaymentSWIFTOut = await subscriptionsStore.currentContextHasFeature(
    PlanFeatures.FeatureInternationalPaymentSWIFTOut
  );
});

async function loadData() {
  state.requiredDataLoading = true;
  if (props.beneficiaryUid) {
    await loadDataFromBeneficiaryUid(props.beneficiaryUid);
  } else if (props.beneficiaryIban) {
    await loadDataFromBeneficiaryIban(props.beneficiaryIban);
  } else if (props.beneficiaryName) {
    state.paymentForm.beneficiary.name = props.beneficiaryName;
  } else if (props.paymentOrder) {
    await loadDataFromPaymentOrderV2(props.paymentOrder);
  } else if (props.paymentOrderPrefill) {
    // Merge any prefill data into the form but only the fields that exists in the paymentForm.
    state.paymentForm = defaults(pick(props.paymentOrderPrefill, keys(state.paymentForm)), state.paymentForm);
    if (props.attachedFileResourceUid) {
      // If we have an auto-attached file resource during drop-scan, load it's data and treat it as a new file resource attachment.
      const resp = await apiGw.get(`/v2/file-resources/${props.attachedFileResourceUid}`);
      state.fileResources = [
        {
          bucketFileName: resp.data.bucketUid,
          displayName: resp.data.displayName,
          fileSize: resp.data.fileSize,
          mimeType: resp.data.mimeType,
          resourceUid: resp.data.uid
        }
      ];
      state.paymentForm.attachedFileUids = [props.attachedFileResourceUid];
    }
  }

  if (!state.beneficiaryIsYapster && state.displayAddressFields && !state.paymentForm.beneficiary.address) {
    state.paymentForm.beneficiary.address = {
      street: '',
      number: '',
      postalCode: '',
      city: '',
      country: state.isIncorePayment ? '' : 'CHE'
    };
  }

  if (state.paymentForm.currency === '') {
    state.paymentForm.currency = state.isIncorePayment ? 'EUR' : 'CHF';
  }

  const cashAccountUid = getCurrentCashAccount.value?.uid;
  if (!cashAccountUid) {
    state.requiredDataLoading = false;
    return;
  }
  await loadSchema(cashAccountUid);
  state.requiredDataLoading = false;
}

watch(
  () => state.submissionError,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      if (newVal?.code === 'invalid_creditor_iban') {
        state.validationErrors.push({
          instancePath: '/body/beneficiary/iban',
          keyword: 'custom',
          message: t('app.payments.invalid_creditor_iban'),
          params: {}
        });
      }
    }
  }
);

watch(
  () => state.paymentForm.beneficiary.iban,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal && state.submissionError?.code === 'invalid_creditor_iban') {
      console.warn(state.submissionError);

      state.validationErrors = state.validationErrors.filter((e) => e.instancePath !== '/body/beneficiary/iban');
      state.submissionError = undefined;
    }
  }
);

watch(
  () => state.beneficiaryIsYapster,
  (newVal) => {
    if (newVal) {
      state.paymentForm.beneficiary.address = undefined;
    }
  }
);

watch(
  () => state.paymentForm.beneficiary.iban,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal && isValidIBAN(newVal)) {
      loadDataFromBeneficiaryIban(newVal);
    }
  }
);

watch(
  () => state.paymentForm.recurrence,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      if (newVal === 'monthlylast') {
        const now = new Date();
        const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        state.paymentForm.paymentTime = lastDayOfMonth.toISOString();
      }
    }
  }
);

async function loadDataFromBeneficiaryUid(beneficiaryUid: string) {
  state.beneficiary = (await apiGw.get(`/beneficiaries/${beneficiaryUid}`)).data;
  if (state.beneficiary) {
    state.paymentForm.beneficiary = merge(state.paymentForm.beneficiary, {
      name: state.beneficiary.name,
      iban: state.beneficiary.iban,
      swiftBIC: state.beneficiary.swiftBIC,
      address: {
        city: state.beneficiary.address?.town,
        country: state.beneficiary.address?.country,
        number: state.beneficiary.address?.houseNo,
        postBoxAddrs: state.beneficiary.address?.poBox,
        postalCode: state.beneficiary.address?.zip,
        street: state.beneficiary.address?.street
      }
    });

    if (state.beneficiary.paymentInputProposal) {
      state.paymentForm.structuredReference = state.beneficiary.paymentInputProposal.reference;
      state.paymentForm.referenceType = state.beneficiary.paymentInputProposal.referenceType;
      state.paymentForm.currency = state.beneficiary.paymentInputProposal.currency;
      state.paymentForm.paymentType = state.beneficiary.paymentInputProposal.paymentType;
      state.paymentForm.paymentReason = state.beneficiary.paymentInputProposal.paymentReason;
    }
  }
}

const refdataStore = useRefdataStore();

async function loadDataFromBeneficiaryIban(beneficiaryIban: string) {
  state.paymentForm.beneficiary.iban = beneficiaryIban;

  if (isYapealIban(beneficiaryIban)) {
    const accountInfo = (await apiGw.get(`/account-number-info?id=${encodeURI(beneficiaryIban)}`)).data;
    if (state.paymentForm.beneficiary.name === '') {
      state.paymentForm.beneficiary.name = accountInfo.name;
    }
  } else {
    if (beneficiaryIban.startsWith('CH')) {
      try {
        const accountInfo = (await apiGw.get(`/account-number-info?id=${encodeURI(beneficiaryIban)}`)).data;
        state.paymentForm.beneficiary = merge(state.paymentForm.beneficiary, {
          name: accountInfo.name,
          address: {
            street: accountInfo.address?.street,
            number: accountInfo.address?.houseNo,
            postalCode: accountInfo.address?.zip,
            city: accountInfo.address?.town,
            country: (accountInfo.address?.country ?? !state.isIncorePayment) ? 'CHE' : undefined
          }
        });
      } catch (error) {
        console.error('Could not load account info', error);
      }
    } else {
      const countryCode = await refdataStore.countryAlpha2ToAlpha3(beneficiaryIban.substring(0, 2));
      if (
        countryCode !== state.paymentForm.beneficiary.address?.country &&
        !state.paymentForm.beneficiary.unstructuredAddress
      ) {
        state.paymentForm.beneficiary.address = {
          street: '',
          number: '',
          postalCode: '',
          city: '',
          country: countryCode
        };
      }
    }
  }
}

function handleFileResourceUploaded(
  files: Array<{
    bucketUid: string;
    displayName: string;
    fileSize: number;
    mimeType: string;
    uid: string;
  }>
) {
  state.paymentForm.attachedFileUids = files.map((f) => f.uid);

  state.fileResources = files.map((f) => {
    return {
      bucketFileName: f.bucketUid,
      displayName: f.displayName,
      fileSize: f.fileSize,
      mimeType: f.mimeType,
      resourceUid: f.uid
    };
  });
}

function handleFileResourceDeleted(deletedUid: string) {
  state.paymentForm.attachedFileUids = state.paymentForm.attachedFileUids?.filter((uid) => uid !== deletedUid);
  state.fileResources = state.fileResources?.filter((f) => f.resourceUid !== deletedUid);
}

async function loadDataFromPaymentOrderV2(paymentOrder: PendingPaymentOrderInfo) {
  const shouldApplyExpectedExecutionDate =
    paymentOrder.status !== PaymentOrderStatus.rejected && !paymentOrder.paymentOrderData?.paymentTime;

  state.paymentForm.beneficiary = merge(state.paymentForm.beneficiary, {
    uid: paymentOrder.paymentOrderData.beneficiary.uid,
    name: paymentOrder.paymentOrderData.beneficiary.name,
    iban: paymentOrder.paymentOrderData.beneficiary.iban,
    swiftBIC: paymentOrder.paymentOrderData.beneficiary.swiftBIC,
    address: paymentOrder.paymentOrderData.beneficiary.addressLines
      ? undefined
      : paymentOrder.paymentOrderData.beneficiary.address,
    unstructuredAddress: paymentOrder.paymentOrderData.beneficiary.addressLines
      ? {
          addressLines: paymentOrder.paymentOrderData.beneficiary.addressLines,
          country: paymentOrder.paymentOrderData.beneficiary.address?.country
        }
      : undefined
  });
  state.paymentForm.beneficiary.uid = paymentOrder.paymentOrderData.beneficiary.uid;
  state.paymentForm.amount = factoredYapAmount(paymentOrder.paymentOrderData.paymentAmount.amount) as string;
  state.paymentForm.currency = paymentOrder.paymentOrderData.paymentAmount.currency;
  state.paymentForm.structuredReference = paymentOrder.paymentOrderData.structuredReference;
  state.paymentForm.paymentReason = paymentOrder.paymentOrderData.paymentReason;
  state.paymentForm.recurrence =
    paymentOrder.paymentOrderData.recurrence && `${paymentOrder.paymentOrderData.recurrence}` !== ''
      ? paymentOrder.paymentOrderData.recurrence
      : 'single';
  state.paymentForm.noteText = paymentOrder.transactionReceiptData?.noteText ?? '';
  if (paymentOrder.transactionReceiptData?.attachedFiles) {
    state.fileResources = paymentOrder.transactionReceiptData.attachedFiles.map((f: any) => {
      return {
        bucketFileName: f.bucketFileName,
        displayName: f.displayName,
        mimeType: f.mime,
        resourceUid: f.resourceUid
      };
    });
  }
  state.paymentForm.category = paymentOrder.paymentOrderData.userCategory ?? 'general';

  state.paymentForm.paymentTime = shouldApplyExpectedExecutionDate
    ? (paymentOrder.paymentOrderData.expectedExecutionTime ?? paymentOrder.paymentOrderData.expectedExecutionDate) // TODO: remove fallback to expectedExecutionDate when BE is fixed
    : paymentOrder.paymentOrderData.paymentTime;

  state.paymentForm.paymentType =
    paymentOrder.paymentOrderData.paymentType ?? paymentOrder.paymentOrderData.paymentOrderType;
  state.paymentForm.e2eReference = paymentOrder.paymentOrderData.e2eReference;
  state.paymentForm.referenceType = paymentOrder.paymentOrderData.referenceType;
  state.paymentForm.accountUid = paymentOrder.paymentOrderData.originator.accountUid;

  if (paymentOrder.paymentOrderData.isEBillOrder || paymentOrder.paymentOrderData.isEBillOrder) {
    try {
      const invoiceUri = '/invoices/' + paymentOrder.paymentOrderData.ebillBCId?.split('/INID/')[0];
      if (invoiceUri) {
        state.invoice = (await apiGw.get(invoiceUri)).data;
      }
    } catch (error) {
      console.error('Could not load invoice details', error);
    }
  } else {
    state.invoice = undefined;
  }
}

async function loadDataForInternalPayment(cashAccountUid: string) {
  const internalBeneficiary = partnershipsStore.currentPartnership;
  const iban = cashAccountsStore.getIbanForCashAccountUid(cashAccountUid);
  if (!iban) {
    return;
  }
  state.paymentForm.beneficiary = {
    name: internalBeneficiary?.name?.name ?? '',
    iban: iban,
    address: undefined,
    unstructuredAddress: undefined
  };
}

watch(
  () => props.paymentOrder,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      loadData();
    }
  }
);

watch(
  () => props.paymentOrderPrefill,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      loadData();
    }
  }
);

watch(
  () => props.beneficiaryUid,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      loadData();
    }
  }
);

watch(
  () => getCurrentCashAccount.value?.uid,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      loadSchema(newVal);
    }
  }
);

watch(
  () => beneficiaryCashAccountUid.value,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      loadDataForInternalPayment(newVal);
    }
  }
);

const { canCreatePaymentOnSelectedCashAccount } = usePaymentEntitlement();
watch(
  () => canCreatePaymentOnSelectedCashAccount(),
  (newVal) => {
    // Only redirect if we have a definitive "false" result and not null (loading)
    if (newVal === false) {
      if (isEnvDevOrBelow()) console.warn('Entitlement to create payment is false -> redirecting to dashboard page');
      router.push({ name: RouteNames.Dashboard });
    }
  }
);

async function loadSchema(cashAccountUid: string) {
  state.schemaLoading = true;
  state.schema = await schemaStore.getSchema({ path: `/v2/cash-accounts/${cashAccountUid}/iban-payment-orders` });
  state.schemaLoading = false;
}

function handleModalClosed() {
  if (state.paymentSubmitted) {
    router.push({ name: RouteNames.Dashboard });
    if (state.formMethod === Method.post) {
      emits('payment-submitted');
    }
    emits('submitted-and-modal-closed');
  }
}

function handleCloseModal(closeModal: any) {
  closeModal();
  handleModalClosed();
}

function prepareFormData(isDraft = false) {
  const data: PaymentFormData = removeEmptyStringFromObject(state.paymentForm);
  // round the amount to 2 decimal places
  data.amount = parseFloat(data.amount).toFixed(2);
  if (!data.beneficiary.address?.postalCode || data.beneficiary.address?.postalCode === '') {
    delete data.beneficiary.address;
  }
  if (state.formMethod === Method.patch) {
    data.accountUid = selectedCashAccountUid.value;
  }

  data.isDraftPO = isDraft;

  if (state.isIncorePayment) {
    data.paymentType = PaymentType.InternationalIBAN;
  }

  return data;
}

async function paymentFormSubmitted(response: any) {
  // In case of an IBAN payment to a new beneficiary, the user may need to perform further actions on the app.
  // In such a case we should inform the user about it.
  if (response.data.status === 'blocked') {
    state.beneficiaryApprovalRequired = true;
    state.beneficiaryUidToBeApproved = getUidsFromUri(response.data._links.beneficiary.uri)[0];
  }

  if (state.formMethod === Method.patch) {
    emits('payment-updated');
  } else {
    emits('payment-submitted');
  }
  state.paymentSubmitted = true;
}

async function handleDelete(closeModal: any) {
  if (state.canDeletePaymentOrder && state.paymentOrder) {
    const action = deleteAction(state.paymentOrder.uid);
    await apiGw[action.method](action.uri);
    closeModal();
    emits('payment-deleted');
  }
}

watch(
  () => state.invoice,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      loadBiller();
    }
  }
);

async function loadBiller() {
  state.loadingStandingApprovals = true;
  if (state.invoice?._links['related-billers']) {
    state.relatedBillerUid = getUidsFromUri(state.invoice._links['related-billers'].uri)[0];
  }
  let relatedBillerUri;
  if (contextSwitchStore.isPrivateContext) {
    relatedBillerUri = createResourceUri(NaturalPersonURI.Base, {
      uid: userStore.uid,
      resource: NaturalPersonURI.RelatedBillers
    });
  } else {
    await partnershipsStore.loadPartnershipVirtualIdentitiesForCurrentPartnership();
    const ctx = contextSwitchStore.getCurrentContext();
    relatedBillerUri = partnershipsStore.hasEbillActivatedForCurrentPartnership
      ? createResourceUri(PartnershipURI.Base, {
          uid: ctx?.uid,
          resource: PartnershipURI.RelatedBillers
        })
      : undefined;
  }
  if (relatedBillerUri) {
    const relatedBillers: Array<Biller> = (await apiGw.get(relatedBillerUri)).data;
    state.biller = relatedBillers.filter((b) => b._links.biller?.uri === state.invoice?._links.biller.uri)[0];
  }
  state.loadingStandingApprovals = false;
}

function standingApprovalUpdated(closeModal: () => void) {
  loadBiller();
  closeModal();
}

async function handleCancelPayment() {
  if (!!state.paymentOrderUid) {
    await apiGw.delete(`/payment-orders/${state.paymentOrderUid}`);
  }
  router.push({ name: RouteNames.NewPayment });
}
</script>
